import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import StarRating from "../components/StarRating";
import { Plus, Star, AlertCircle, Pencil, Save, X, Eye, EyeOff, Lock, Check, Shield, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Klaida";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Profile() {
  const { t } = useI18n();
  const { user, login, refresh } = useAuth();
  const [listings, setListings] = useState([]);
  const [ratings, setRatings] = useState({ average: 0, count: 0, ratings: [] });

  // edit state
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  // password state
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setPhone(user.phone || "");
    setAddress(user.address || "");
    api.get("/listings/mine").then((r) => setListings(r.data)).catch(()=>{});
    api.get(`/ratings/user/${user.user_id}`).then((r) => setRatings(r.data)).catch(()=>{});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-20 px-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C85A32]"/>
        <p className="mt-4 font-heading font-bold text-2xl">{t("login_required")}</p>
        <button onClick={login} data-testid="profile-login-button" className="mt-6 btn-primary">{t("cta_login")}</button>
      </div>
    );
  }

  const saveProfile = async () => {
    if (!name.trim()) { toast.error("Vardas privalomas"); return; }
    setSaving(true);
    try {
      await api.patch("/auth/profile", { name, phone, address });
      await refresh();
      toast.success("Profilis atnaujintas");
      setEditing(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  const cancelEdit = () => {
    setName(user.name || "");
    setPhone(user.phone || "");
    setAddress(user.address || "");
    setEditing(false);
  };

  const changePassword = async () => {
    if (newPw.length < 6) { toast.error("Slaptažodis bent 6 simb."); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Slaptažodis pakeistas");
      setCurPw(""); setNewPw(""); setPwOpen(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
    setPwSaving(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Header card */}
      <div className="card-surface p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-full"/>
          ) : (
            <div className="w-16 h-16 rounded-full bg-[#1A1A18] text-white flex items-center justify-center text-2xl font-bold">
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-heading font-black text-[#1A1A18]" data-testid="profile-name">{user.name}</h1>
            <p className="text-sm text-[#7A7A73] truncate" data-testid="profile-email">{user.email}</p>
            {ratings.count > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <StarRating value={ratings.average} size={14} readOnly testId="profile-rating" />
                <span className="text-xs text-[#7A7A73]">{ratings.average} ({ratings.count} {t("reviews")})</span>
              </div>
            )}
          </div>
          {!editing && (
            <button
              data-testid="edit-profile-button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#1A1A18] rounded-md text-sm font-semibold hover:bg-[#1A1A18] hover:text-white transition-colors"
            >
              <Pencil className="w-4 h-4"/> Redaguoti
            </button>
          )}
        </div>

        {editing && (
          <div className="mt-6 pt-6 border-t border-[#D1CFC7] space-y-4" data-testid="edit-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[#7A7A73] mb-1.5">
                Vardas (matomas pirkėjams)
              </label>
              <input
                data-testid="edit-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Pvz. Jonas P."
                className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[#C85A32]"
              />
              <p className="text-xs text-[#7A7A73] mt-1.5">El.paštas: <strong>{user.email}</strong> (negali būti pakeistas)</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-[#7A7A73] mb-1.5">Telefonas</label>
                <input
                  data-testid="edit-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+370 60011222"
                  className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[#C85A32]"
                />
                <p className="text-xs text-[#7A7A73] mt-1 inline-flex items-center gap-1"><EyeOff className="w-3 h-3"/>Privatus – matomas tik tau</p>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-[#7A7A73] mb-1.5">Adresas</label>
                <input
                  data-testid="edit-address-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Vilnius"
                  className="w-full bg-white border border-[#D1CFC7] rounded-md px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[#C85A32]"
                />
                <p className="text-xs text-[#7A7A73] mt-1 inline-flex items-center gap-1"><EyeOff className="w-3 h-3"/>Privatus – matomas tik tau</p>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                data-testid="save-profile-button"
                onClick={saveProfile}
                disabled={saving}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4"/>{saving ? "..." : "Išsaugoti"}
              </button>
              <button
                data-testid="cancel-edit-button"
                onClick={cancelEdit}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <X className="w-4 h-4"/> Atšaukti
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Privacy notice */}
      <div className="bg-[#2A5C38] text-white rounded-md p-4 mb-6 flex items-start gap-3" data-testid="privacy-notice">
        <Shield className="w-5 h-5 flex-shrink-0 mt-0.5"/>
        <div className="text-sm">
          <p className="font-bold mb-1">Tavo privatumas saugus 🔒</p>
          <p className="text-white/85">
            Pirkėjai mato <strong>tik tavo vardą ir avatarą</strong>. El. paštas, telefonas ir adresas yra <strong>privatūs</strong> ir niekada nerodomi kitiems vartotojams. Susirašinėjimas vyksta tik per platformą – savo kontaktais dalintis nebūtina.
          </p>
        </div>
      </div>

      {/* Password change card */}
      <div className="card-surface p-5 mb-8">
        <button
          onClick={() => setPwOpen(!pwOpen)}
          data-testid="toggle-password-section"
          className="w-full flex items-center justify-between"
        >
          <span className="font-heading font-bold text-[#1A1A18] inline-flex items-center gap-2">
            <Lock className="w-4 h-4"/> Pakeisti slaptažodį
          </span>
          <span className="text-[#7A7A73] text-sm">{pwOpen ? "−" : "+"}</span>
        </button>
        {pwOpen && (
          <div className="mt-4 space-y-3">
            <div className="relative">
              <input
                data-testid="current-password-input"
                type={showPw ? "text" : "password"}
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                placeholder="Dabartinis slaptažodis (jei turi)"
                className="w-full bg-white border border-[#D1CFC7] rounded-md px-3 py-2.5 pr-10 outline-none focus:ring-2 focus:ring-[#C85A32]"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-2.5 text-[#7A7A73]" data-testid="toggle-show-pw">
                {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
            <input
              data-testid="new-password-input"
              type={showPw ? "text" : "password"}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="Naujas slaptažodis (bent 6 simb.)"
              className="w-full bg-white border border-[#D1CFC7] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
            <button
              data-testid="save-password-button"
              onClick={changePassword}
              disabled={pwSaving}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Check className="w-4 h-4"/>{pwSaving ? "..." : "Išsaugoti naują slaptažodį"}
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading font-bold text-[#1A1A18]">{t("your_listings")}</h2>
        <Link to="/post" className="btn-secondary inline-flex items-center gap-2 !py-2 !px-4 !text-sm" data-testid="profile-new-listing">
          <Plus className="w-4 h-4"/>{t("nav_post")}
        </Link>
      </div>
      {listings.length === 0 ? (
        <div className="border border-dashed border-[#D1CFC7] rounded-md p-10 text-center text-[#7A7A73]">
          {t("listings_empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map((l) => (
            <div key={l.id} className="card-surface flex items-center gap-4 p-3" data-testid={`profile-listing-${l.id}`}>
              <Link to={`/listing/${l.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-20 h-20 bg-[#F0EFEB] rounded-md overflow-hidden flex-shrink-0">
                  {l.images?.[0] && <img src={fileUrl(l.images[0])} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-[#1A1A18] truncate">{l.title}</p>
                  <p className="text-sm text-[#7A7A73]">{l.city} · {l.price.toFixed(2)} €</p>
                </div>
              </Link>
              <Link
                to={`/post/${l.id}`}
                data-testid={`profile-edit-${l.id}`}
                className="p-2 rounded-md border border-[#D1CFC7] hover:bg-[#1A1A18] hover:text-white hover:border-[#1A1A18] transition-colors"
                title="Redaguoti"
              >
                <Edit3 className="w-4 h-4"/>
              </Link>
              {l.bumped_at && (
                <span className="tag bg-[#C85A32] text-white">↑ TOP</span>
              )}
            </div>
          ))}
        </div>
      )}

      {ratings.ratings.length > 0 && (
        <div className="mt-10">
          <h2 className="text-xl font-heading font-bold text-[#1A1A18] mb-4 flex items-center gap-2">
            <Star className="w-5 h-5"/>{t("reviews")}
          </h2>
          <div className="space-y-3">
            {ratings.ratings.map((r) => (
              <div key={r.id} className="border border-[#D1CFC7] rounded-md p-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-[#1A1A18]">{r.rater_name}</span>
                  <StarRating value={r.stars} size={14} readOnly testId={`review-stars-${r.id}`} />
                </div>
                {r.review && <p className="text-sm text-[#4A4A45]">{r.review}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
